'use client';

import { useEffect, useRef } from 'react';
import Icon from '@/components/ui/AppIcon';

const advantages = [
{
  icon: 'ClockIcon',
  title: 'Beschikbaar 24u/24, 7d/7',
  desc: 'Weekends en feestdagen inbegrepen. Uw noodgeval is onze prioriteit.'
},
{
  icon: 'BoltIcon',
  title: 'Snelle interventietijd',
  desc: 'Wij interveniëren binnen 2u in uw zone voor noodgevallen.'
},
{
  icon: 'CurrencyEuroIcon',
  title: 'Vaste tarieven, geen verrassingen',
  desc: 'De prijs wordt schriftelijk bevestigd vóór aanvang van de werken. Nooit overschreden.'
},
{
  icon: 'ShieldCheckIcon',
  title: '2 jaar garantie',
  desc: 'Al onze interventies zijn 2 jaar gegarandeerd op onderdelen en arbeid.'
},
{
  icon: 'AcademicCapIcon',
  title: 'Gecertificeerde loodgieters',
  desc: 'Gekwalificeerd, verzekerd en ervaren team. Gecertificeerd voor gas en sanitair.'
},
{
  icon: 'DocumentTextIcon',
  title: 'Gratis & schriftelijke offerte',
  desc: 'Gedetailleerde offerte zonder verbintenis, verzonden per e-mail of WhatsApp.'
}];


const steps = [
{
  num: '01',
  icon: 'PhoneIcon',
  title: 'Contact',
  desc: 'Bel ons of vul het formulier in. Beschikbaar 24u/24.'
},
{
  num: '02',
  icon: 'DocumentCheckIcon',
  title: 'Schriftelijke offerte',
  desc: 'U ontvangt een gedetailleerde offerte vóór elke interventie. Geen verrassing.'
},
{
  num: '03',
  icon: 'WrenchScrewdriverIcon',
  title: 'Interventie',
  desc: 'Onze technici interveniëren snel met het juiste materiaal.'
},
{
  num: '04',
  icon: 'CheckCircleIcon',
  title: 'Betaling na validatie',
  desc: 'U valideert het uitgevoerde werk vóór betaling. Uw tevredenheid eerst.'
}];


export default function AdvantagesProcess() {
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('active');
          }
        });
      },
      { threshold: 0.12 }
    );
    const elements = sectionRef.current?.querySelectorAll('.reveal-up, .section-line');
    elements?.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} id="avantages" className="px-5 py-5" style={{ background: 'rgba(245,247,250,0.8)' }}>
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 items-start">

          {/* Left: Advantages */}
          <div className="lg:col-span-7">
            <div className="section-line mb-8" />
            <span className="text-xs font-bold uppercase tracking-[0.4em] mb-4 block reveal-up" style={{ color: '#16A34A' }}>
              Waarom ons kiezen
            </span>
            <h2
              className="font-display leading-tight mb-12 reveal-up"
              style={{ fontSize: 'clamp(1.8rem,4vw,3rem)', fontWeight: 700, color: '#1A2B3C' }}>

              De betrouwbare loodgieter,{' '}
              <span className="font-display italic" style={{ color: '#16A34A', fontWeight: 400 }}>
                zonder compromis.
              </span>
            </h2>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 stagger-children">
              {advantages.map((adv) =>
              <div
                key={adv.title}
                className="advantage-item glass-card rounded-2xl p-5 reveal-up group"
                style={{ border: '1px solid rgba(0,0,0,0.07)' }}>

                  <div
                  className="advantage-icon w-10 h-10 rounded-xl flex items-center justify-center mb-4"
                  style={{ background: 'rgba(232,96,10,0.1)' }}>

                    <Icon name={adv.icon as any} size={20} style={{ color: '#16A34A' }} />
                  </div>
                  <h3 className="font-semibold text-base mb-2" style={{ color: '#1A2B3C' }}>{adv.title}</h3>
                  <p className="text-sm leading-relaxed" style={{ color: '#7A95AD' }}>
                    {adv.desc}
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Right: Process */}
          <div className="lg:col-span-5">
            <div
              className="glass-card rounded-3xl p-8 reveal-up"
              style={{ border: '1px solid rgba(232,96,10,0.2)', position: 'sticky', top: '100px', background: 'rgba(232,96,10,0.04)' }}>

              <span className="text-xs font-bold uppercase tracking-[0.4em] mb-4 block" style={{ color: '#16A34A' }}>
                Hoe het werkt
              </span>
              <h3
                className="font-display font-semibold leading-tight mb-8"
                style={{ fontSize: '1.75rem', color: '#1A2B3C' }}>

                Eenvoudig en{' '}
                <span className="font-display italic" style={{ color: '#16A34A', fontWeight: 400 }}>
                  transparant.
                </span>
              </h3>

              <div className="space-y-6">
                {steps.map((step, i) =>
                <div key={step.num} className="flex gap-4 relative">
                    {/* Connector line */}
                    {i < steps.length - 1 &&
                  <div
                    className="absolute left-5 top-10 w-[1px] h-8"
                    style={{ background: 'linear-gradient(to bottom, rgba(232,96,10,0.3), transparent)' }} />

                  }
                    {/* Step circle */}
                    <div
                    className="w-10 h-10 rounded-full flex items-center justify-center shrink-0 font-bold text-xs"
                    style={{
                      background: 'rgba(232,96,10,0.1)',
                      border: '1px solid rgba(22,163,74,0.25)',
                      color: '#16A34A'
                    }}>

                      {step.num}
                    </div>
                    <div className="pt-1">
                      <div className="flex items-center gap-2 mb-1">
                        <Icon name={step.icon as any} size={14} style={{ color: '#16A34A' }} />
                        <h4 className="font-semibold text-sm" style={{ color: '#1A2B3C' }}>{step.title}</h4>
                      </div>
                      <p className="text-sm leading-relaxed" style={{ color: '#7A95AD' }}>
                        {step.desc}
                      </p>
                    </div>
                  </div>
                )}
              </div>

              {/* CTA */}
              <a
                href="tel:+32496538283"
                className="btn-primary shine-sweep mt-8 flex items-center justify-center gap-3 py-4 rounded-full font-bold text-sm w-full">

                <Icon name="PhoneIcon" size={18} className="text-white" />
                Nu bellen
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>);

}